import React, { useState, useContext } from 'react';
import { Button } from '@mui/material';
import PageInstructions from '@shared/components/PageInstructions';
import StickyHeader from '@shared/components/StickyHeader';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import { Page } from '../../../../layout';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import PrivilegeNames from '../../../../../constants/privilegeNames';
import { userInfo } from '@apollo-internal/cache';
import { useMutation, useReactiveVar } from '@apollo/client';
import EDIT_EVENT from '../../../queries/EditEventMutation';
import DELETE_EVENT from '../../../queries/DeleteEventMutation';
import REVIEW_EVENT from '../../../queries/reviewEventMutation';
import ApolloError from '@shared/components/ApolloError';
import EventInputValidator from '../../../validators/EventInputValidator';
import { useNavigate } from 'react-router-dom';
import CalendarEventMapper from '../../../mappers/CalendarEventMapper';
import { APPRAISER_EVENT_STAGE } from '../../../constants/AppraiserEventStage';
import { isEqual, some } from 'lodash';
import WarningComponent from '../../shared/WarningComponent';
import { EVENT_WARNING_TYPES, WARNING_SCOPE } from '../../../constants/EventWarningTypes';
import { getEventWarningWithScope } from '../../../services/calendarWarningService';
import useEventBidding from '../../../hooks/useEventBidding';
import useSaveAssignmentAction from '../../../hooks/useSaveAssignmentAction';
import useSaveObservationAction from '../../../hooks/useSaveObservationAction';
import useConfirmAssignmentAction from '../../../hooks/useConfirmAssignmentAction';
import CommentSection from './EventComments/CommentSection';
import { PAGE_MODE, EVENT_APPRAISER_ASSIGNMENT_STATUS } from '../../shared/constants';
import AssignmentButtons from './AssignmentButtons/AssignmentButtons';
import { useSelectedDateStore } from '../../../CalendarStore';
import LinkButton from '@shared/components/LinkButton';
import useDateChangeRequest from '../../shared/hooks/useDateChangeRequest';
import { EVENT_APPRAISER_ASSIGNMENT_ROLE } from '../../../constants/EventAppraiserAssignmentRole';
import FINALIZE_EVENT from '../../../queries/FinalizeEventMutation';
import { getCanFinalizeEvent } from '../../../services/calendarFinalizeService';
import { DELIVERY_METHODS } from '../../../constants/DeliveryMethodsType';
import { EVENT_REGION_MAPPING } from '../../../constants/EventRegionMapping';
import { showErrorToast, showSuccessToast } from '@shared/services/toastService';
import READY_EVENT from '../../../queries/readyEventMutation';
import STATUS_TYPES from './SowStatus/constants/statusTypes';
import CONTRACT_STATUSES from './SowStatus/constants/contractStatuses';
import { getEventTeamRoleAssignmentSettings } from '../../../services/eventAssignmentTableService';
import { EventSettingsContext } from '../../shared/contexts/EventSettingsContext';
import SUBMIT_EVENT from '../../../queries/SubmitEventMutation';
import EventReturnActionButton from './EventReturn/components/EventReturnActionButton';
import { userAssignmentService } from './shared/userAssignmentService';
import AssignmentButtonsWithComment from './AssignmentButtons/AssignmentButtonsWithComment';

const OPERATION_TYPES = {
  NONE: 'none',
  SUBMIT: 'submit',
  READY: 'ready',
  REVIEW: 'review',
  EDIT: 'edit',
  FINALIZE: 'finalize'
};

const previousLink = '/calendar';
const pageTitle = 'Edit Event';

const editSuccessMessage = 'Event edited successfully.';
const submitSuccessMessage = 'Event submitted successfully.';
const finalizeSuccessMessage = 'Event finalized successfully.';
const reviewEventApproveText = 'approved';

const readySuccessMessage = 'Event Moved to Ready to Deliver Successfully.';
const readyConfirmMessage = 'Are you sure this event is ready?';

/**
 * @typedef {object} EditEventComponentWrapperEventFormData
 * @property {Object} [atlObserver]
 * @property {Array<*>} [eventTeams]
 * @property {Array<*>} [eventAssignments]
 * @property {Array<*>} [atmObservationAssignments]
 * @property {number} [deliveryMethod]
 * @property {Object} [regions]
 * @property {Array<*>} [atlPreferences]
 * @property {Date} [startDate]
 */

/**
 * @typedef {object} EditEventComponentWrapperEventUserAssignmentPendingActions
 * @property {boolean} [ObservationAssignmentActionRequired]
 */

/**
 * @typedef {object} EditEventComponentWrapperEventTargetUserEventParticipationInfo
 * @property {boolean} [IsEventParticipant]
 */

/**
 * @typedef {object} EditEventComponentWrapperEvent
 * @property {number} [Stage]
 * @property {boolean} [AtmAssignmentActionRequired]
 * @property {boolean} [AtmAssignmentPendingConfirmation]
 * @property {EditEventComponentWrapperEventUserAssignmentPendingActions} [UserAssignmentPendingActions]
 * @property {Object} [AppraiserEventChangeDateRequest]
 * @property {Array<*>} [WarningTypes]
 * @property {Array<*>} EventFlags
 * @property {EditEventComponentWrapperEventTargetUserEventParticipationInfo} [TargetUserEventParticipationInfo]
 * @property {Array<*>} [EventAssignments]
 * @property {Array<*>} [AppraiserEventComments]
 * @property {Array<*>} [UserCommentAccessibilityOptions]
 */

/**
 * @typedef {object} EditEventComponentWrapperOrgUnitsData
 * @property {Object} [getSelectedOrgForEvent]
 */

/**
 * @typedef {object} EditEventComponentWrapperProps
 * @property {number} eventIdParsed
 * @property {number} pageMode
 * @property {EditEventComponentWrapperEventFormData} eventFormData
 * @property {EditEventComponentWrapperEvent} [event]
 * @property {EditEventComponentWrapperOrgUnitsData} [orgUnitsData]
 * @property {boolean} [orgUnitsLoading]
 * @property {boolean} [eventLoading]
 * @property {(eventFormData: Object | ((prev: Object) => Object)) => void} setEventFormData
 * @property {Array<*>} [eventAssignments]
 */

/**
 * @param {EditEventComponentWrapperProps} props
 */
const EditEventComponentWrapper = props => {
  const {
    eventIdParsed,
    pageMode,
    eventFormData,
    event,
    orgUnitsData,
    orgUnitsLoading,
    eventLoading,
    setEventFormData
  } = props;

  const navigate = useNavigate();
  const currentUserInfo = useReactiveVar(userInfo);
  const privileges = currentUserInfo.Privileges;

  const setSelectedDate = useSelectedDateStore.getState().setSelectedDate;

  const [validationMode, setValidationMode] = useState(OPERATION_TYPES.NONE);
  const [validationErrors, setValidationErrors] = useState({});

  const breadcrumbPathItems = [
    { name: 'Calendar', url: previousLink },
    { name: 'Edit Event', url: '' }
  ];

  const settings = useContext(EventSettingsContext);

  const EditEventComponent = settings.component;

  const eventStage = event?.Stage;

  const showFinalizeButton =
    settings.canFinalize && eventStage === APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID;

  const showReadyButton =
    settings.canReadyEvent && eventStage === APPRAISER_EVENT_STAGE.TEAM_ASSIGNED;

  const { canBidForEvent, isUserBidForEvent, handleBidOnEvent, handleRemoveBid, eventBidError } =
    useEventBidding(eventIdParsed, settings.canBidOnEvent);

  const showAssignmentButtons = pageMode === PAGE_MODE.ATM && event?.AtmAssignmentActionRequired;

  const showConfirmationAssignmentButtons =
    pageMode === PAGE_MODE.ATM && event?.AtmAssignmentPendingConfirmation;

  const showObservervationAssignmentButtons =
    (pageMode === PAGE_MODE.ASSIGNED_ATL_OBSERVER || pageMode === PAGE_MODE.ATM) &&
    event?.UserAssignmentPendingActions.ObservationAssignmentActionRequired;

  const changeDateRequestData = event?.AppraiserEventChangeDateRequest;
  const dateRequestStatus = event?.AppraiserEventChangeDateRequest?.Status;
  const atlDateWarning = getEventWarningWithScope(event?.WarningTypes, WARNING_SCOPE.ATL_DATE);

  const teamRoleAssignmentSettings = getEventTeamRoleAssignmentSettings(eventFormData?.eventTeams);
  const filteredAssignmentData = filterValidEventAssignments(
    eventFormData,
    event?.EventAssignments,
    teamRoleAssignmentSettings
  );

  const isFinalizeDisabled =
    showFinalizeButton &&
    !getCanFinalizeEvent(eventFormData, dateRequestStatus, filteredAssignmentData);

  const isEventMustBeRescheduled =
    !privileges.includes(PrivilegeNames.Calendar_EditEvent_EditRescheduleWarning) &&
    atlDateWarning === EVENT_WARNING_TYPES.APPRAISAL_MUST_BE_RESCHEDULED;

  const showAtlPreferences =
    privileges.includes(PrivilegeNames.Calendar_EditEvent_EditAtlPreferences) &&
    eventStage === APPRAISER_EVENT_STAGE.DRAFT;

  const isReviewEnabled =
    eventStage === APPRAISER_EVENT_STAGE.ATL_ASSIGNED_SUBMITTED &&
    privileges.includes(PrivilegeNames.Calendar_SubmitEventReview);

  const orgData = orgUnitsData ? [orgUnitsData.getSelectedOrgForEvent] : [];

  const [deleteEvent, { error: deleteEventError }] = useMutation(DELETE_EVENT);
  const [editEvent, { error: editEventError }] = useMutation(EDIT_EVENT);
  const [submitEvent, { error: submitEventError }] = useMutation(SUBMIT_EVENT);
  const [reviewEvent, { error: reviewEventError }] = useMutation(REVIEW_EVENT);
  const [finalizeEvent, { error: finalizeEventError }] = useMutation(FINALIZE_EVENT);

  const [readyEvent, { error: readyEventError }] = useMutation(READY_EVENT);

  const { handleSubmitAssignmentAction, error: saveAssignmentActionError } =
    useSaveAssignmentAction(eventIdParsed, event);

  const { handleSaveObservationAction, error: saveObservationActionError } =
    useSaveObservationAction(eventIdParsed);

  const { handleConfirmAssignmentAction, error: confirmAssignmentActionError } =
    useConfirmAssignmentAction(eventIdParsed, eventFormData);

  const { dateChangeRequestError, handleSubmitDataRequest, handleChangeDateRequestReviewed } =
    useDateChangeRequest(eventIdParsed, changeDateRequestData, setEventFormData);

  const handleEdit = () => handleEventOperation(OPERATION_TYPES.EDIT);
  const handleSubmit = () => handleEventOperation(OPERATION_TYPES.SUBMIT);
  const handleFinalize = () => handleEventOperation(OPERATION_TYPES.FINALIZE);
  const handleReady = () => handleEventOperation(OPERATION_TYPES.READY);

  const OPERATION_CONFIGS = {
    [OPERATION_TYPES.SUBMIT]: {
      operation: submitEvent,
      validator: (formData, eventData) =>
        EventInputValidator.validateSubmitEventData(formData, eventData),
      successMessage: submitSuccessMessage,
      displayFieldErrors: true
    },
    [OPERATION_TYPES.READY]: {
      operation: readyEvent,
      validator: (formData, eventData) =>
        EventInputValidator.validateReadyEventData(formData, eventData),
      successMessage: readySuccessMessage,
      displayFieldErrors: true
    },
    [OPERATION_TYPES.REVIEW]: {
      operation: reviewEvent,
      validator: formData => EventInputValidator.validateReviewEventData(formData),
      successMessage: null,
      displayFieldErrors: true
    },
    [OPERATION_TYPES.EDIT]: {
      operation: editEvent,
      validator: (formData, eventData, options) =>
        EventInputValidator.validateEditEventData(formData, eventData, options),
      successMessage: editSuccessMessage,
      displayFieldErrors: false
    },
    [OPERATION_TYPES.FINALIZE]: {
      operation: finalizeEvent,
      validator: (formData, eventData, options) =>
        EventInputValidator.validateEditEventData(formData, eventData, options),
      successMessage: finalizeSuccessMessage,
      displayFieldErrors: false
    }
  };

  const handleEventOperation = async mode => {
    setValidationMode(mode);
    const config = OPERATION_CONFIGS[mode];

    const validationStatus = config.validator(eventFormData, event, {
      pageMode
    });

    if (validationStatus.messages.length) {
      validationStatus.messages.forEach(message => {
        showErrorToast(message);
      });

      if (config.displayFieldErrors) {
        handleValidationErrors(validationStatus.errors);
      }

      return;
    }
    resetValidationErrors();

    const filteredEventAssignments = getEventAssignments(
      teamRoleAssignmentSettings,
      eventFormData.eventAssignments,
      event?.EventAssignments,
      eventFormData.deliveryMethod,
      eventFormData.regions
    );
    const filteredEventFormData = {
      ...eventFormData,
      eventAssignments: filteredEventAssignments,
      atlPreferences: showAtlPreferences
        ? eventFormData.atlPreferences?.filter(atlPreference => atlPreference.rank) || []
        : null,
      atlObserver: settings.canEditAtlObserver ? eventFormData.atlObserver : null,
      atmObservationAssignments: getAtmObservationAssignments(
        eventFormData.atmObservationAssignments,
        filteredEventAssignments
      )
    };

    await config.operation({
      variables: {
        event: CalendarEventMapper.toApolloEditEventInput(filteredEventFormData)
      }
    });

    setSelectedDate(eventFormData.startDate);
    showSuccessToast(config.successMessage);
    navigate(previousLink);
  };

  const handleEventDelete = async () => {
    await deleteEvent({ variables: { id: eventIdParsed } });
    showSuccessToast('Event deleted successfully.');
    navigate(previousLink);
  };

  const handleEventReview = async (stage, reviewActionText, notes) => {
    await reviewEvent({
      variables: {
        eventReview: {
          EventId: eventIdParsed,
          Stage: stage,
          Notes: notes
        }
      }
    });

    showSuccessToast(`Appraisal details ${reviewActionText} successfully.`);
    navigate(previousLink);
  };

  const handleEventFormDataChanges = formDataChanges => {
    setEventFormData(prevFormData => {
      const updatedFormData = formDataChanges(prevFormData);

      const updatedData = handleAtlChanges(prevFormData, updatedFormData, event);

      const displayFieldErrors = OPERATION_CONFIGS[validationMode]?.displayFieldErrors;

      if (displayFieldErrors) {
        isFormValidAndHandleErrors(updatedData, validationMode, event);
      }

      return updatedData;
    });
  };

  const getOnDateRangeChangedHandler = (startDateName, endDateName) => (startDate, endDate) => {
    setEventFormData(prevState => ({
      ...prevState,
      [startDateName]: startDate,
      [endDateName]: endDate
    }));
  };

  const handleValidationErrors = validationError => {
    const errors = getValidationErrors(validationError);

    if (!isEqual(errors, validationErrors)) {
      setValidationErrors(errors);
    }
  };

  const resetValidationErrors = () => {
    setValidationErrors({});
  };

  const isFormValidAndHandleErrors = (formData, mode, eventData) => {
    const validationConfig = OPERATION_CONFIGS[mode];
    const validationStatus = validationConfig.validator(formData, eventData);

    if (validationStatus.errors) {
      handleValidationErrors(validationStatus.errors);
      return { result: false, messages: validationStatus.messages };
    }

    resetValidationErrors();
    return { result: true, messages: [] };
  };

  const handleOpenReadyEventPreCondition = () => {
    setValidationMode(OPERATION_TYPES.READY);

    const { result: isFormValid, messages } = isFormValidAndHandleErrors(
      eventFormData,
      OPERATION_TYPES.READY,
      settings.sowSettings.editOnlyQualifiedAtms
    );

    if (!isFormValid) {
      messages.forEach(message => {
        showErrorToast(message);
      });
    }

    const newPrimaryAssignments = getNewPrimaryAssignments(filteredAssignmentData);

    newPrimaryAssignments?.forEach(assignment => {
      showErrorToast(
        `ATM ${assignment.name} needs to accept the assignment before moving event to the next stage.`
      );
    });

    return isFormValid && !newPrimaryAssignments?.length;
  };

  const handleOpenReviewEventPreCondition = () => {
    setValidationMode(OPERATION_TYPES.REVIEW);
    const { result } = isFormValidAndHandleErrors(eventFormData, OPERATION_TYPES.REVIEW);
    return result;
  };

  if (
    editEventError ||
    submitEventError ||
    deleteEventError ||
    reviewEventError ||
    dateChangeRequestError ||
    saveAssignmentActionError ||
    confirmAssignmentActionError ||
    saveObservationActionError ||
    eventBidError ||
    finalizeEventError ||
    readyEventError
  ) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='event-page'>
      <Breadcrumb pathItems={breadcrumbPathItems} />
      <StickyHeader>
        <div className='page-header'>
          <PageInstructions
            loading={pageMode === PAGE_MODE.TBD}
            skeletonClassName='instruction-skeleton'
            title={pageTitle}
            description={settings.description}
          />
          <div className='action-group'>
            <WarningComponent
              warningType={atlDateWarning}
              eventId={eventIdParsed}
              renderIconAsTitle={false}
              customClassName='warning-icon-centered'
            />
            <div className='action-button-group' data-testid='event-buttons-group'>
              <LinkButton className='secondary-button sm' to={previousLink}>
                Cancel
              </LinkButton>

              {settings.isEditEnabled && (
                <Button
                  data-testid='save-event-btn'
                  className='primary-button sm'
                  onClick={handleEdit}>
                  Save
                </Button>
              )}

              {showReadyButton && (
                <ConfirmDialogWithButton
                  handleOnConfirm={handleReady}
                  openModalBtnText='Ready'
                  openModalBtnClassName='sm ready'
                  message={readyConfirmMessage}
                  openModalPreCondition={handleOpenReadyEventPreCondition}
                  cancelBtnText='Cancel'
                  confirmBtnText='Ready'
                />
              )}

              {showFinalizeButton && (
                <Button
                  disabled={isFinalizeDisabled}
                  className='sm finalize'
                  onClick={handleFinalize}>
                  Finalize
                </Button>
              )}

              {settings.isSubmitEnabled && (
                <Button
                  disabled={!settings.isEditEnabled || isEventMustBeRescheduled}
                  className='primary-button sm'
                  onClick={handleSubmit}>
                  Submit
                </Button>
              )}

              {settings.canDeleteEvent && (
                <ConfirmDialogWithButton
                  handleOnConfirm={handleEventDelete}
                  openModalBtnText='Delete'
                  openModalBtnClassName='delete-button'
                  message='Are you sure you want to delete this event? This action cannot be undone.'
                />
              )}

              {canBidForEvent && !isUserBidForEvent && (
                <Button className='primary-button sm' onClick={handleBidOnEvent}>
                  Bid
                </Button>
              )}

              {isUserBidForEvent && (
                <Button className='sm remove-bid' onClick={handleRemoveBid}>
                  Remove Bid
                </Button>
              )}

              {showAssignmentButtons && (
                <AssignmentButtonsWithComment onSubmit={handleSubmitAssignmentAction} />
              )}

              {showObservervationAssignmentButtons && (
                <AssignmentButtons onAction={handleSaveObservationAction} />
              )}

              {showConfirmationAssignmentButtons && (
                <AssignmentButtons
                  acceptLabel='Confirm'
                  rejectLabel='Decline'
                  onAction={handleConfirmAssignmentAction}
                />
              )}

              {isReviewEnabled && (
                <>
                  <ConfirmDialogWithButton
                    handleOnConfirm={async () =>
                      await handleEventReview(
                        APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID,
                        reviewEventApproveText
                      )
                    }
                    openModalBtnText='Approve'
                    confirmBtnText='Approve'
                    cancelBtnText='Cancel'
                    openModalBtnType='secondary-button'
                    openModalBtnClassName='approve-button'
                    message='Are you sure you want to approve this event?'
                    openModalPreCondition={handleOpenReviewEventPreCondition}
                  />
                  <EventReturnActionButton
                    handleConfirm={handleEventReview}
                    openModalPreCondition={handleOpenReviewEventPreCondition}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </StickyHeader>
      <EditEventComponent
        eventId={eventIdParsed}
        eventLoading={eventLoading}
        orgUnitsLoading={orgUnitsLoading}
        eventFormData={eventFormData}
        storedEventData={event}
        setEventFormData={handleEventFormDataChanges}
        getOnDateRangeChangedHandler={getOnDateRangeChangedHandler}
        organizations={orgData}
        validationErrors={validationErrors}
        disableDateEdit={isEventMustBeRescheduled}
        dateRequestStatus={dateRequestStatus}
        handleSubmitDataRequest={handleSubmitDataRequest}
        changeDateRequestData={changeDateRequestData}
        handleChangeDateRequestReviewed={handleChangeDateRequestReviewed}
        isAtmView={pageMode === PAGE_MODE.ATM}
        filteredAssignmentData={filteredAssignmentData}
        pageMode={pageMode}
      />
      {event?.TargetUserEventParticipationInfo.IsEventParticipant && (
        <CommentSection
          eventId={eventIdParsed}
          loading={eventLoading}
          eventComments={event?.AppraiserEventComments || []}
          accessibilityOptions={event?.UserCommentAccessibilityOptions || []}
        />
      )}
    </Page>
  );
};

const getValidationErrors = joiError => {
  if (!joiError?.details) {
    return {};
  }

  return joiError.details.reduce((errors, { path }) => {
    path.reduce((current, key, index) => {
      if (index === path.length - 1) {
        current[key] = true;
        return current[key];
      }

      current[key] = current[key] || (Number.isInteger(path[index + 1]) ? [] : {});
      return current[key];
    }, errors);

    return errors;
  }, {});
};

const getEventAssignments = (
  teamRoleAssignmentSettings,
  assignments,
  existingEventAssignments,
  deliveryMethod,
  eventRegions
) =>
  assignments?.filter(
    assignment =>
      (assignment.assignmentRank || assignment.role !== EVENT_APPRAISER_ASSIGNMENT_ROLE.NA) &&
      filterAssignmentsByNumberOfPeopleForRole(teamRoleAssignmentSettings, assignment) &&
      filterEventAssignmentsByRegion(
        existingEventAssignments,
        assignment,
        deliveryMethod,
        eventRegions
      )
  );

const getAtmObservationAssignments = (atmObservationAssignments, filteredEventAssignments) => {
  if (!atmObservationAssignments) {
    return null;
  }

  if (!filteredEventAssignments) {
    return atmObservationAssignments;
  }

  const assignedUserIds = filteredEventAssignments.map(x => x.externalUserId);

  return atmObservationAssignments.filter(
    x =>
      assignedUserIds.includes(x.observerExternalUserId) &&
      assignedUserIds.includes(x.observedExternalUserId)
  );
};

const isNewAssignmentStatus = assignment =>
  !assignment.status || assignment.status === EVENT_APPRAISER_ASSIGNMENT_STATUS.ASSIGNED;

const isPrimaryRole = assignment => assignment.role === EVENT_APPRAISER_ASSIGNMENT_ROLE.PRIMARY;

const getNewPrimaryAssignments = filteredAssignmentData =>
  filteredAssignmentData?.filter(
    assignment => isPrimaryRole(assignment) && isNewAssignmentStatus(assignment)
  );

const filterValidEventAssignments = (eventFormData, eventAssignments, teamRoleAssignmentSettings) =>
  eventFormData?.eventAssignments?.filter(
    assignment =>
      filterAssignmentsByNumberOfPeopleForRole(teamRoleAssignmentSettings, assignment) &&
      filterEventAssignmentsByRegion(
        eventAssignments,
        assignment,
        eventFormData.deliveryMethod,
        eventFormData.regions
      )
  );

const filterAssignmentsByNumberOfPeopleForRole = (teamRoleAssignmentSettings, assignment) =>
  teamRoleAssignmentSettings.some(
    x => x.role === assignment.teamRole && x.hasPositiveNumberOfPeople
  );

//TODO: Refactor - Filter the ATM assignments once by the region in MBRD-1967
const filterEventAssignmentsByRegion = (eventAssignments, assignment, deliveryMethod, regions) =>
  isUserAssigned(eventAssignments, assignment) ||
  isUserWillingToTravelToRegions(deliveryMethod, regions, assignment);

const isUserAssigned = (eventAssignments, assignment) => {
  const eventAssignment = eventAssignments?.find(
    assign => assign.Appraiser.ExternalUserId === assignment.externalUserId
  )?.EventAppraiserAssignment;

  if (!eventAssignment) {
    return false;
  }

  const { ActiveRole: role, ActiveStatus: status } = eventAssignment;

  return (
    userAssignmentService.isPrimaryAndAcceptedAssignment(role, status) ||
    role === EVENT_APPRAISER_ASSIGNMENT_ROLE.SECONDARY
  );
};

const isUserWillingToTravelToRegions = (
  eventFormDataDeliveryMethod,
  eventFormDataRegions,
  assignment
) =>
  eventFormDataDeliveryMethod === DELIVERY_METHODS.VIRTUAL.value ||
  some(eventFormDataRegions, (region, eventRegionField) => {
    const regionField = EVENT_REGION_MAPPING[eventRegionField];
    return region && assignment[regionField];
  });

const handleAtlChanges = (prevFormData, updatedFormData, storedEventData) => {
  const currentAtlValue = updatedFormData.atl?.value;
  const previousAtlValue = prevFormData.atl?.value;

  if (previousAtlValue === currentAtlValue) {
    return updatedFormData;
  }

  const isRevertingToOriginalAtl = storedEventData?.Atl?.ExternalUserId === currentAtlValue;
  const originalSowStatuses = storedEventData?.SowStatuses || [];

  const updatedSowStatuses = updatedFormData.sowStatuses?.map(status =>
    status.statusType === STATUS_TYPES.ATL
      ? updateAtlStatus(status, isRevertingToOriginalAtl, originalSowStatuses)
      : status
  );

  return {
    ...updatedFormData,
    sowStatuses: updatedSowStatuses
  };
};

const updateAtlStatus = (status, isRevertingToOriginalAtl, originalSowStatuses) => {
  if (isRevertingToOriginalAtl) {
    const originalStatus = originalSowStatuses.find(
      origStatus => origStatus.StatusType === STATUS_TYPES.ATL
    );
    return originalStatus
      ? {
          ...status,
          contractDate: originalStatus.ContractDate ? new Date(originalStatus.ContractDate) : null,
          contractTypes: originalStatus.ContractTypes,
          contractStatus: originalStatus.ContractStatus
        }
      : status;
  }

  return {
    ...status,
    contractDate: null,
    contractTypes: [],
    contractStatus: CONTRACT_STATUSES.PENDING_CREATION
  };
};

export default EditEventComponentWrapper;
